import { OperationResponse } from "api/actor"
import { BrokerDto } from "api/broker"
import manSearchBig from "assets/images/icons/dashboard/man-search-big.svg"
import newMain from "assets/images/icons/dashboard/new-main.svg"
import { PersonSearchBar } from "features/subscription-process/components/person-search/PersonSearchBar"
import { SearchFeatureFilter, SearchPersonResult } from "components/domain/SearchBar/types"
import { NsKey } from "enums/NsKey"
import { useDynamicTranslation } from "hooks/useDynamicTranslation"
import { ChevronLeftIcon } from "lucide-react"
import { FC, useCallback, useState } from "react"
import { BrokerForm } from "../brokers-view/form/BrokerForm"
import { BrokerFormValues } from "../brokers-view/form/BrokerFormValues"
import { BrokerFormVariant } from "../brokers-view/form/BrokerLayout"
import { BrokerSummary } from "../brokers-view/BrokerSummary"

interface PlateformeBrokerLinkerProps {
  onSubmit?: (values: BrokerFormValues) => Promise<OperationResponse | undefined>
  onLinkBroker: (brokerId: string) => void
}

const VIEW_STATE = {
  SEARCH: "search",
  FORM: "form",
  SUMMARY: "summary",
} as const

type ViewState = (typeof VIEW_STATE)[keyof typeof VIEW_STATE]

export const PlateformeBrokerLinker: FC<PlateformeBrokerLinkerProps> = ({ onSubmit, onLinkBroker }) => {
  const { t } = useDynamicTranslation([NsKey.ActorForm, NsKey.Common])
  const [selectedBroker, setSelectedBroker] = useState<BrokerDto>()
  const [viewState, setViewState] = useState<ViewState>(VIEW_STATE.SEARCH)

  const handleShowAddForm = useCallback(() => {
    setSelectedBroker(undefined)
    setViewState(VIEW_STATE.FORM)
  }, [])

  const handleReset = useCallback(() => {
    setSelectedBroker(undefined)
    setViewState(VIEW_STATE.SEARCH)
  }, [])

  const handleSelectBroker = useCallback((person: SearchPersonResult | null) => {
    if (person) {
      setSelectedBroker(person.data as BrokerDto)
      setViewState(VIEW_STATE.SUMMARY)
    }
  }, [])

  const renderBackButton = () => (
    <button className="flex items-center gap-2" onClick={handleReset}>
      <ChevronLeftIcon className="h-5 w-5" />
      <span>{t("Revenir à la recherche d'un courtier")}</span>
    </button>
  )

  if (viewState === VIEW_STATE.SUMMARY && selectedBroker) {
    return (
      <div className="space-y-4">
        {renderBackButton()}
        <BrokerSummary broker={selectedBroker} onLinkBroker={() => onLinkBroker(selectedBroker.id)} />
      </div>
    )
  }

  if (viewState === VIEW_STATE.FORM) {
    return (
      <div className="space-y-4">
        {renderBackButton()}
        <BrokerForm variant={BrokerFormVariant.Plateforme} onSubmit={onSubmit} />
      </div>
    )
  }

  return (
    <div className="flex w-full flex-col gap-8 px-4 py-14 text-center">
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-center">
          <img src={manSearchBig} alt="Recherche de courtier" />
        </div>
        <h2 className="text-lg font-bold text-gray-900">
          {t("Quel courtier souhaitez-vous intégrer dans la plateforme ?")}
        </h2>
      </div>

      <div className="mx-auto w-full max-w-2xl">
        <PersonSearchBar
          featureFilter={SearchFeatureFilter.Broker}
          withLegalEntity
          onSelect={handleSelectBroker}
        />
      </div>

      <button
        onClick={handleShowAddForm}
        className="mx-auto flex w-full max-w-2xl flex-col items-center gap-4 rounded-2xl border bg-white p-8 shadow transition-shadow hover:shadow-md"
      >
        <div className="flex items-center justify-center">
          <img src={newMain} alt="Créer un courtier" />
        </div>
        <p className="font-bold text-gray-900">
          {t("Créer un nouveau courtier dans la plateforme")}
        </p>
      </button>
    </div>
  )
}
